import React from 'react';
import { CandidateOpportunitiesConst } from '@axiom/const';
import { Badge, Layout } from '@axiom/ui';
import { candidateOpportunityStatusUtils } from '@axiom/utils';
import type {
  MLRecommendation,
  Opportunity,
  OpportunityCandidate,
} from '@axiom/validation';

import { CandidateUtil } from '../../utils/candidate-util';

const {
  AllStatusLabels: AllStatusLabelsConst,
  CandidateStatuses,
  MLRecommendationStatuses,
} = CandidateOpportunitiesConst;
const AllStatusLabels: Record<string, string> = AllStatusLabelsConst;

type OpportunityTalentTileBadgesProps = {
  isPriorityTalent: OpportunityCandidate['isPriorityTalent'];
  opportunity: Opportunity;
  opportunityCandidateOrMLRecommendation:
    OpportunityCandidate | MLRecommendation;
};

export const OpportunityTalentTileBadges = ({
  isPriorityTalent,
  opportunity,
  opportunityCandidateOrMLRecommendation,
}: OpportunityTalentTileBadgesProps) => {
  const {
    candidateId,
    candidateStatus,
    isCrossFulfilled,
    previousCandidateStatus,
  } = opportunityCandidateOrMLRecommendation;

  const isNew =
    candidateStatus === CandidateStatuses.Interested ||
    candidateStatus === MLRecommendationStatuses.AiSuggested;

  const isSelfSubmit = !!(
    opportunity.selfService &&
    candidateStatus === CandidateStatuses.Submitted &&
    previousCandidateStatus === CandidateStatuses.Interested
  );

  const needsCooled =
    candidateStatus === CandidateStatuses.Rejected ||
    (opportunity.isClosed &&
      candidateOpportunityStatusUtils
        .getAllowedTransitions({
          currentCandidateStatus: candidateStatus,
          opportunity,
          position: undefined,
          role: undefined,
        })
        .includes(CandidateStatuses.Cooled));

  const showCrossFulfilledBadge =
    !!isCrossFulfilled && !['Engaged', 'Selected'].includes(candidateStatus);

  return (
    <Layout
      name={`${candidateId}-tile-badges`}
      position="right top"
      horizontalGutter="4px"
      verticalGutter="4px"
      wrap
    >
      {!!isPriorityTalent && (
        <Badge background="forest green" name={`${candidateId}-priority-badge`}>
          Priority
        </Badge>
      )}
      {!!showCrossFulfilledBadge && (
        <Badge background="yellow" name={`${candidateId}-cross-badge`}>
          Cross
        </Badge>
      )}
      {!!isNew && (
        <Badge background="blue" name={`${candidateId}-new-badge`}>
          New
        </Badge>
      )}
      {!!needsCooled && (
        <Badge background="yellow" name={`${candidateId}-needs-cooled-badge`}>
          Needs Cooled
        </Badge>
      )}
      {!!isSelfSubmit && (
        <Badge background="gray" name={`${candidateId}-self-submitted-badge`}>
          Self-Sub
        </Badge>
      )}
      {candidateStatus ? (
        <Badge
          background={CandidateUtil.getCandidateStatusBadgeColor(
            candidateStatus
          )}
          name={`${candidateId}-candidate-status-badge`}
        >
          {AllStatusLabels[candidateStatus]}
        </Badge>
      ) : null}
    </Layout>
  );
};
